import { useSyncExternalStore } from 'react'
import { registerSW } from 'virtual:pwa-register'

// Actualización del service worker en modo AVISO (no silencioso).
//
// Por qué no 'autoUpdate': camp carga chunks grandes en diferido (tiptap, firestore).
// Si el SW cambia los assets bajo los pies de alguien que tiene la app abierta, al
// abrir el editor pediría un chunk con hash que ya no existe y el import dinámico
// falla. Con aviso, la recarga la decide el usuario y ocurre en un momento seguro.
//
// Mismo patrón de store que lib/install.ts: estado a nivel de módulo + listeners,
// porque el registro debe ocurrir una sola vez y fuera del árbol de React.

let needRefresh = false
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

// updateSW(true) activa el SW en espera y recarga la página.
let updateSW: ((reload?: boolean) => Promise<void>) | null = null

if (import.meta.env.PROD) {
  updateSW = registerSW({
    onNeedRefresh() {
      needRefresh = true
      emit()
    },
    // Registro best-effort (patrón Carpenter): si falla, la app sigue online.
    onRegisterError() {},
  })
}

/**
 * El servidor retiró una ruta porque esta app es de una versión anterior (410
 * `version_vieja_app`). Prende el mismo aviso que prendería el service worker al
 * detectar un bundle nuevo.
 *
 * POR QUÉ HACE FALTA ADEMÁS DEL SERVICE WORKER: el aviso del SW depende de que el
 * navegador revise el registro, y una pestaña que lleva horas abierta puede tardar
 * en enterarse. El 410 es la señal más temprana y más cierta que existe: ya
 * chocamos contra un servidor que no nos reconoce. Prender el aviso aquí convierte
 * un error mudo en la única acción que sirve, que es recargar.
 */
export function marcarVersionVieja(): void {
  if (needRefresh) return
  needRefresh = true
  emit()
}

export function applyUpdate(): void {
  needRefresh = false
  emit()
  void updateSW?.(true)
}

export function dismissUpdate(): void {
  needRefresh = false
  emit()
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => listeners.delete(cb)
}
const getSnapshot = () => needRefresh
const getServerSnapshot = () => false

export function useNeedRefresh(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
